/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { Link } from '@/components/link';
import { SeoMeta } from '@/components/seo-meta';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

function StatementSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
    return (
        <section aria-labelledby={title.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')} className="space-y-4">
            <h2
                id={title.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}
                className="font-display text-2xl font-light text-foreground md:text-3xl">
                {title}
            </h2>
            <div className="space-y-4 leading-relaxed text-muted-foreground">{children}</div>
        </section>
    );
}

export default function WebsiteAccessibility(): ReactElement {
    return (
        <div className="pb-16">
            <SeoMeta
                title="Website Accessibility — Baggallini"
                description="Read Baggallini's commitment to digital accessibility and learn how to contact us with accessibility questions or feedback."
            />

            <div className="mx-auto max-w-screen-2xl px-4 pb-2 pt-4">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Website Accessibility</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <main className="mx-auto max-w-screen-md px-6 pt-8 md:pt-10">
                <article className="space-y-10">
                    <header>
                        <h1 className="font-display text-4xl font-light tracking-wide text-foreground md:text-5xl">
                            Baggallini Digital Accessibility Statement
                        </h1>
                    </header>

                    <StatementSection title="Our Policy & Commitment">
                        <p>
                            At Baggallini, we are committed to accessibility, diversity, and inclusion. As part of that
                            commitment, we are working to ensure that Baggallini&apos;s digital experiences are
                            accessible to all individuals, regardless of ability. Our goal is to conform to applicable
                            accessibility standards, including the W3C&apos;s Web Content Accessibility Guidelines
                            (WCAG) 2.1 Level AA.
                        </p>
                    </StatementSection>

                    <StatementSection title="Digital Accessibility Statement">
                        <p>
                            We are working to ensure our websites and online services are accessible for everyone. We
                            are committed to following the W3C{' '}
                            <a
                                href="https://www.w3.org/WAI/standards-guidelines/wcag/"
                                className="font-medium text-foreground underline underline-offset-4 hover:no-underline">
                                Web Content Accessibility Guidelines
                            </a>{' '}
                            and other applicable web accessibility laws.
                        </p>
                        <p>
                            To support this work, we have partnered with{' '}
                            <a
                                href="https://www.levelaccess.com/"
                                className="font-medium text-foreground underline underline-offset-4 hover:no-underline">
                                Level Access
                            </a>{' '}
                            and use its digital accessibility platform and expertise to evaluate our digital experiences
                            against accessibility best practices. This work is supported by a diverse team of
                            accessibility professionals, including people with disabilities.
                        </p>
                        <p>
                            Through this partnership, Baggallini continues to develop its digital accessibility program
                            and capabilities so that accessibility is considered when we design, develop, and test our
                            websites and online services.
                        </p>
                    </StatementSection>

                    <StatementSection title="Contact">
                        <p>
                            We want to hear from you. If you would like more information about Baggallini&apos;s
                            accessibility efforts, have an accessibility question or feedback, or encounter an
                            accessibility barrier while using our digital experiences, please contact us at:
                        </p>
                        <p>
                            <a
                                href="mailto:ecommerce@rgbarry.com"
                                className="font-medium text-foreground underline underline-offset-4 hover:no-underline">
                                ecommerce@rgbarry.com
                            </a>
                        </p>
                    </StatementSection>

                    <p className="border-t border-border pt-6 text-sm text-muted-foreground">
                        Last updated: September 4, 2026
                    </p>
                </article>
            </main>
        </div>
    );
}
